import React, { useState } from "react";

import { useMutation } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { isEmpty } from "lodash";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { PlanFragment } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { formatISODate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import EmptyPlan from "./components/EmptyPlan";
import TabContent from "./components/TabContent";
import Plan from "./Plan";
import { planListPageQuery } from "./PlanListPage";

type PlanListProp = {
  currentPrograms: PlanFragment[];
  upcomingPrograms: PlanFragment[];
  finishedPrograms: PlanFragment[];
};

const useStyles = makeStyles()(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  list: {
    display: "flex",
    flexDirection: "row",
  },
  tab: {
    textTransform: "none",
    fontSize: "18px",
  },
}));

const deleteProgramMutation = graphql(`
  mutation DeleteProgram($id: ID!) {
    deleteProgram(input: { id: $id }) {
      program {
        id
        client {
          id
          status
        }
      }
    }
  }
`);

const PlanList: React.FC<PlanListProp> = ({
  currentPrograms,
  upcomingPrograms,
  finishedPrograms,
}) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const [tabValue, setTabValue] = useState<number>(0);
  const { clientId } = useParams<{ clientId: string }>();
  const [deleteProgram] = useMutation(deleteProgramMutation);

  const handleDeleteProgram = async (id: string) => {
    try {
      await deleteProgram({
        variables: { id },
        refetchQueries: [
          {
            query: planListPageQuery,
            variables: {
              clientId: clientId ?? "",
              currentDate: formatISODate(new Date()),
              name: "",
            },
          },
        ],
        awaitRefetchQueries: true,
      });
      snackbar.open("Plan has been deleted.");
    } catch (e) {
      snackbar.open(displayError(e));
    }
  };

  const renderFinishedPlans = () => {
    if (isEmpty(finishedPrograms)) return <EmptyPlan planType="finished" />;

    return finishedPrograms?.map((item) => (
      <Plan key={item.id} id={item.id} onDelete={handleDeleteProgram} />
    ));
  };

  const renderUpcomingPlans = () => {
    if (isEmpty(upcomingPrograms)) return <EmptyPlan planType="upcoming" />;

    return upcomingPrograms?.map((item) => (
      <Plan key={item.id} id={item.id} onDelete={handleDeleteProgram} />
    ));
  };

  const renderCurrentPlans = () => {
    if (isEmpty(currentPrograms)) return <EmptyPlan planType="current" />;

    return currentPrograms?.map((item) => (
      <Plan
        key={item.id}
        id={item.id}
        onDelete={handleDeleteProgram}
        isCurrentPlan
      />
    ));
  };

  return (
    <Box className={classes.root}>
      <Typography variant="subtitle1" color="textSecondary" fontSize="18px">
        Current Plan
      </Typography>
      {renderCurrentPlans()}
      <Tabs
        value={tabValue}
        indicatorColor="primary"
        textColor="primary"
        onChange={(_, newValue: number) => {
          setTabValue(newValue);
        }}
      >
        <Tab label="Upcoming plans" className={classes.tab} />
        <Tab label="Finished plans" className={classes.tab} />
      </Tabs>
      <TabContent value={tabValue} index={0}>
        {renderUpcomingPlans()}
      </TabContent>
      <TabContent value={tabValue} index={1}>
        {renderFinishedPlans()}
      </TabContent>
    </Box>
  );
};

export default PlanList;
